// src/pages/FaelligeWartungen.jsx
// Büro-Liste: Welche Wartungen und Sichtprüfungen muss ich vereinbaren?
//
// Anna-Lena 15.09.2026 (Vorschlag 9) und 17.09.2026 (Vertrags-Tool):
// Faellig ab = letzte Wartung + Zyklus. Zyklus aus dem Wartungsvertrag (Migration
// 45), sonst der an der Station hinterlegte, sonst der vom Monteur empfohlene -
// sind mehrere bekannt, zaehlt der kuerzere Monteur-Vorschlag. Gab es noch keine
// Wartung, gilt der erste Wartungstermin laut Vertrag.
// Sichtpruefungen kommen aus dem Wartungsvertrag (naechsteSichtpruefung).
// Stationen OHNE Vertrag sind die Kandidaten zum Ansprechen ("Angesprochen").
//
// Braucht Migration 40; ohne Migration 45 laeuft die Liste weiter, nur ohne
// Vertragsangaben.

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase, withTimeout } from '../lib/supabase';
import { C, zurueckStil } from './formui';
import { fahrAnschrift } from '../hooks/useWartungen';
import VertraegeEditor from './VertraegeEditor';
import {
  alleVertraege, istMigrationFehlt, naechsteSichtpruefung, planungAusVertrag, datumAus,
  ABSCHALTZEITEN, MONATE_KURZ, MONATE_LANG,
} from '../lib/vertraege';

const OFFEN_STATUS = ['vorbereitet', 'zugewiesen', 'entwurf'];
const SEITE = 1000;   // PostgREST liefert hoechstens 1000 Zeilen je Anfrage
const SPALTEN = 'id, bezeichnung, stations_nr, standort_strasse, standort_plz, standort_ort, '
  + 'objekt_abweichend, objekt_name, objekt_strasse, objekt_plz, objekt_ort, '
  + 'letzte_wartung, wartungs_zyklus, empfohlener_zyklus_jahre, wartungsvertrag, vertrag_notiz, '
  + 'kunde_angesprochen_am, kunde_angesprochen_notiz, objekt_ansprechpartner, objekt_telefon, objekt_email, '
  + 'kunden(id, name, kunden_nr, telefon, mobil, email, ansprechpartner)';

const heute = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const isoHeute = () => {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
};
const monatJahr = (d) => (d ? String(d.getMonth() + 1).padStart(2, '0') + '/' + d.getFullYear() : '–');
// Faelligkeit nur mit Monat und Jahr (Anna-Lena 15.09.2026).
const monatName = (d) => (d ? MONATE_LANG[d.getMonth()] + ' ' + d.getFullYear() : '–');
const tagMonatJahr = (iso) => { const d = datumAus(iso); return d ? String(d.getDate()).padStart(2, '0') + '.' + monatJahr(d).replace('/', '.') : ''; };

function zyklusDerStation(st) {
  const m = String(st.wartungs_zyklus || '').match(/^(\d+)/);
  return m ? Number(m[1]) : null;
}

function gruppeVon(faellig) {
  if (!faellig) return 'unbekannt';
  const h = heute();
  const dieserMonat = new Date(h.getFullYear(), h.getMonth(), 1);
  const in3 = new Date(h.getFullYear(), h.getMonth() + 3, 1);
  const in12 = new Date(h.getFullYear(), h.getMonth() + 12, 1);
  return faellig < dieserMonat ? 'ueberfaellig' : faellig <= in3 ? 'bald' : faellig <= in12 ? 'jahr' : 'spaeter';
}

// Eine Station ergibt einen Eintrag "Wartung" und ggf. einen "Sichtprüfung".
function eintraegeDerStation(st, geplant, vertraege) {
  const w = vertraege?.wartung?.vorhanden ? vertraege.wartung : null;
  const letzte = datumAus(st.letzte_wartung);
  const zVertrag = w?.wartungszyklus_jahre || null;
  const zStation = zVertrag || zyklusDerStation(st);
  const zEmpf = st.empfohlener_zyklus_jahre || null;
  // Empfiehlt der Monteur einen KUERZEREN Zyklus, soll die Station frueher auftauchen.
  const zyklus = zStation && zEmpf ? Math.min(zStation, zEmpf) : (zStation || zEmpf);
  let faellig = null;
  if (letzte && zyklus) faellig = new Date(letzte.getFullYear() + zyklus, letzte.getMonth(), 1);
  else if (!letzte && w?.erste_wartung) { const e = datumAus(w.erste_wartung); faellig = new Date(e.getFullYear(), e.getMonth(), 1); }
  const basis = { station: st, vertraege: vertraege || {}, wartungsvertrag: w, letzte, zStation, zEmpf, zVertrag, geplant: geplant || [],
    // Ohne Migration 45 gilt weiter das alte Haekchen an der Station.
    hatVertrag: !!w || (!vertraege && !!st.wartungsvertrag) };
  // Geplante getrennt nach Art: Eine geplante Sichtpruefung ist keine geplante Wartung.
  const geplantWartung = basis.geplant.filter(p => p.art !== 'sichtpruefung');
  const geplantSicht = basis.geplant.filter(p => p.art === 'sichtpruefung');
  const liste = [{ ...basis, geplant: geplantWartung, typ: 'wartung', faellig, gruppe: gruppeVon(faellig) }];
  const sicht = naechsteSichtpruefung(w, letzte, faellig);
  if (sicht) liste.push({ ...basis, geplant: geplantSicht, typ: 'sicht', faellig: sicht, gruppe: gruppeVon(sicht) });
  return liste;
}

const GRUPPE = {
  ueberfaellig: { text: 'überfällig', farbe: C.red, bg: C.redBg },
  bald:         { text: 'in den nächsten 3 Monaten', farbe: C.orange, bg: C.orangeBg },
  jahr:         { text: 'in den nächsten 12 Monaten', farbe: C.navy, bg: C.gray100 },
  spaeter:      { text: 'später', farbe: C.gray500, bg: C.gray100 },
  unbekannt:    { text: 'ohne Datum (letzte Wartung oder Zyklus fehlt)', farbe: C.gray500, bg: C.gray100 },
};

const ZEITRAUM = [
  { key: 'faellig', text: 'Fällig: überfällig + 3 Monate' },
  { key: 'jahr', text: 'Fällig in 12 Monaten' },
  { key: 'monat', text: 'Monat / Jahr …' },
  { key: 'unbekannt', text: 'ohne Fälligkeit' },
  { key: 'alle', text: 'alle Zeiträume' },
];
const ART = [
  { key: 'alle', text: 'Wartung + Sichtprüfung' },
  { key: 'wartung', text: 'nur Wartungen' },
  { key: 'sicht', text: 'nur Sichtprüfungen' },
];
const VERTRAG = [
  { key: 'alle', text: 'mit und ohne Vertrag' },
  { key: 'mit', text: 'mit Wartungsvertrag' },
  { key: 'ohne', text: 'ohne Vertrag' },
];

// Ruhige Buero-Ansicht (Anna-Lena, 18.09.2026): kleine Felder, eine Filterleiste,
// Liste statt grosser Karten.
const L = {
  rand: '1px solid ' + C.gray200,
  feld: { height: 34, padding: '0 10px', borderRadius: 6, border: '1px solid ' + C.gray200, fontSize: 13.5, background: C.white,
          color: C.navy, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' },
  knopf: { height: 30, padding: '0 12px', borderRadius: 6, border: 'none', background: C.navy, color: C.white, fontSize: 13,
           fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit' },
  link: { background: 'none', border: 'none', padding: 0, color: C.blue, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
          textDecoration: 'none', fontFamily: 'inherit', whiteSpace: 'nowrap' },
};

export default function FaelligeWartungen({ onBack, onPlanen, onImport }) {
  const [stationen, setStationen] = useState([]);
  const [geplantJeStation, setGeplantJeStation] = useState({});
  const [vertraegeJeStation, setVertraegeJeStation] = useState({});
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState(null);
  const [migrationFehlt, setMigrationFehlt] = useState(false);
  const [vertragMigrationFehlt, setVertragMigrationFehlt] = useState(false);
  const [zeitraum, setZeitraum] = useState('faellig');
  const [jahr, setJahr] = useState(new Date().getFullYear());
  const [monat, setMonat] = useState(0);   // 0 = ganzes Jahr, sonst 1-12
  const [art, setArt] = useState('alle');
  const [vertrag, setVertrag] = useState('alle');
  const [suche, setSuche] = useState('');
  const [geplanteAusblenden, setGeplanteAusblenden] = useState(true);
  const [speichert, setSpeichert] = useState(null);   // station id
  const [vertragOffen, setVertragOffen] = useState(null);   // station
  const ungespeichert = useRef(false);

  const vertraegeLaden = useCallback(async () => {
    try {
      const jeStation = {};
      (await alleVertraege()).forEach(v => { (jeStation[v.station_id] = jeStation[v.station_id] || {})[v.art] = v; });
      setVertraegeJeStation(jeStation);
      setVertragMigrationFehlt(false);
    } catch (e) {
      if (istMigrationFehlt(e)) setVertragMigrationFehlt(true);
      else throw e;
    }
  }, []);

  const laden = useCallback(async () => {
    setLaedt(true); setFehler(null);
    try {
      const alle = [];
      for (let von = 0; ; von += SEITE) {
        const { data, error } = await withTimeout(
          supabase.from('stationen').select(SPALTEN).eq('active', true).order('id').range(von, von + SEITE - 1),
          30000, 'Stationen laden');
        if (error) throw error;
        alle.push(...(data || []));
        if (!data || data.length < SEITE) break;
      }
      const { data: offen, error: e2 } = await withTimeout(
        supabase.from('protokolle').select('station_id, wartungs_datum, status, art').in('status', OFFEN_STATUS),
        30000, 'Geplante Wartungen laden');
      if (e2) throw e2;
      const jeStation = {};
      (offen || []).forEach(p => { if (p.station_id) (jeStation[p.station_id] = jeStation[p.station_id] || []).push(p); });
      await vertraegeLaden();
      setStationen(alle);
      setGeplantJeStation(jeStation);
      setMigrationFehlt(false);
    } catch (e) {
      // 42703 = Spalte gibt es nicht -> Migration 40 noch nicht ausgefuehrt
      if (e?.code === '42703' || /wartungsvertrag|empfohlener_zyklus|angesprochen/.test(String(e?.message || ''))) setMigrationFehlt(true);
      else setFehler(e.message || 'Die Liste konnte nicht geladen werden.');
    } finally {
      setLaedt(false);
    }
  }, [vertraegeLaden]);
  useEffect(() => { laden(); }, [laden]);

  const eintraege = useMemo(
    () => stationen.flatMap(st => eintraegeDerStation(st, geplantJeStation[st.id], vertraegeJeStation[st.id])),
    [stationen, geplantJeStation, vertraegeJeStation]);

  const zaehler = useMemo(() => {
    const z = { ueberfaellig: 0, bald: 0, sicht: 0, ohneVertragFaellig: 0 };
    eintraege.forEach(b => {
      const faelligBald = b.gruppe === 'ueberfaellig' || b.gruppe === 'bald';
      if (b.typ === 'sicht') { if (faelligBald) z.sicht++; return; }
      if (b.geplant.length) return;
      if (b.gruppe === 'ueberfaellig') z.ueberfaellig++;
      if (b.gruppe === 'bald') z.bald++;
      if (faelligBald && !b.hatVertrag) z.ohneVertragFaellig++;
    });
    return z;
  }, [eintraege]);

  const liste = useMemo(() => {
    const s = suche.trim().toLowerCase();
    const passtZeit = (b) => {
      if (zeitraum === 'faellig') return b.gruppe === 'ueberfaellig' || b.gruppe === 'bald';
      if (zeitraum === 'jahr') return ['ueberfaellig', 'bald', 'jahr'].includes(b.gruppe);
      if (zeitraum === 'unbekannt') return b.gruppe === 'unbekannt';
      if (zeitraum === 'monat') return !!b.faellig && b.faellig.getFullYear() === Number(jahr) && (!monat || b.faellig.getMonth() + 1 === Number(monat));
      return true;
    };
    return eintraege
      .filter(passtZeit)
      .filter(b => art === 'alle' || b.typ === art)
      .filter(b => vertrag === 'alle' || (vertrag === 'mit' ? b.hatVertrag : !b.hatVertrag))
      .filter(b => !geplanteAusblenden || b.geplant.length === 0)
      .filter(b => {
        if (!s) return true;
        const st = b.station;
        return [st.kunden?.name, st.kunden?.kunden_nr, st.bezeichnung, st.stations_nr, st.standort_ort, st.standort_plz, st.objekt_ort, st.objekt_name]
          .some(x => String(x || '').toLowerCase().includes(s));
      })
      .sort((a, b) => (a.faellig ? a.faellig.getTime() : Infinity) - (b.faellig ? b.faellig.getTime() : Infinity)
        || String(a.station.kunden?.name || '').localeCompare(String(b.station.kunden?.name || '')));
  }, [eintraege, zeitraum, jahr, monat, art, vertrag, suche, geplanteAusblenden]);

  const stationAendern = async (st, patch) => {
    setSpeichert(st.id);
    try {
      const { error } = await withTimeout(supabase.from('stationen').update(patch).eq('id', st.id), 20000, 'Speichern');
      if (error) throw error;
      setStationen(alt => alt.map(x => (x.id === st.id ? { ...x, ...patch } : x)));
    } catch (e) {
      alert('Nicht gespeichert: ' + (e.message || e));
    } finally {
      setSpeichert(null);
    }
  };

  const angesprochen = (st) => {
    const eingabe = window.prompt('Kunde angesprochen – Notiz (optional, z. B. "Angebot geschickt", "meldet sich im Frühjahr"):', '');
    if (eingabe === null) return;
    stationAendern(st, { kunde_angesprochen_am: isoHeute(), kunde_angesprochen_notiz: eingabe.trim() || null });
  };

  const sichtErledigt = async (b) => {
    const st = b.station;
    if (!window.confirm((st.kunden?.name || '') + ' – ' + (st.bezeichnung || '') + '\n\nSichtprüfung als erledigt eintragen (heute)?')) return;
    setSpeichert(st.id);
    try {
      const { error } = await withTimeout(
        supabase.from('station_vertraege').update({ letzte_sichtpruefung: isoHeute() }).eq('id', b.wartungsvertrag.id),
        20000, 'Speichern');
      if (error) throw error;
      await vertraegeLaden();
    } catch (e) {
      alert('Nicht gespeichert: ' + (e.message || e));
    } finally {
      setSpeichert(null);
    }
  };

  const jahre = [];
  for (let j = new Date().getFullYear() - 1; j <= new Date().getFullYear() + 6; j++) jahre.push(j);

  // Kennzahl anklicken = passender Filter (schneller als drei Auswahlfelder)
  const kennzahlFilter = (schluessel) => {
    if (schluessel === 'ueberfaellig' || schluessel === 'bald') { setZeitraum('faellig'); setArt('wartung'); setVertrag('alle'); }
    if (schluessel === 'sicht') { setZeitraum('faellig'); setArt('sicht'); setVertrag('alle'); }
    if (schluessel === 'ohne') { setZeitraum('faellig'); setArt('wartung'); setVertrag('ohne'); }
  };

  return (
    <div style={{ background: C.bg, minHeight: '100vh', fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif", color: C.navy }}>
      <div style={{ background: C.navy, padding: '12px 14px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={onBack} style={zurueckStil}>‹ Zurück</button>
          <div>
            <div style={{ color: C.white, fontWeight: 700, fontSize: 17 }}>Fällige Wartungen & Verträge</div>
            <div style={{ color: C.onDark, fontSize: 13 }}>Wartungen und Sichtprüfungen, die zu vereinbaren sind</div>
          </div>
          {onImport && (
            <button type="button" onClick={onImport}
              style={{ marginLeft: 'auto', height: 34, padding: '0 14px', borderRadius: 6, border: '1px solid ' + C.navyLight, background: 'transparent', color: C.onDark, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Verträge importieren
            </button>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '16px 14px 40px' }}>
        {migrationFehlt && (
          <div style={{ background: C.yellowBg, borderLeft: '3px solid ' + C.yellow, borderRadius: 6, padding: 12, fontSize: 14, lineHeight: 1.5 }}>
            <strong>Noch nicht eingerichtet:</strong> Für diese Liste muss in Supabase zuerst die Datei
            <code> 40-wartungsvertrag-faelligkeit.sql</code> ausgeführt werden.
          </div>
        )}
        {vertragMigrationFehlt && !migrationFehlt && (
          <div style={{ background: C.yellowBg, borderLeft: '3px solid ' + C.yellow, borderRadius: 6, padding: 10, fontSize: 14, marginBottom: 12 }}>
            Verträge und Sichtprüfungen erscheinen erst, wenn in Supabase die Datei <code>45-vertraege.sql</code> ausgeführt ist.
          </div>
        )}
        {fehler && (
          <div style={{ background: C.redBg, borderRadius: 6, padding: 12, fontSize: 14, marginBottom: 12 }}>
            {fehler} <button type="button" onClick={laden} style={{ ...L.link, marginLeft: 8 }}>Nochmal laden</button>
          </div>
        )}
        {laedt && <div style={{ padding: 30, textAlign: 'center', color: C.gray500, fontSize: 14 }}>Stationen werden geladen …</div>}

        {!laedt && !migrationFehlt && !fehler && (
          <>
            {/* Kennzahlen: eine schmale Leiste, anklickbar */}
            <div style={{ display: 'flex', flexWrap: 'wrap', background: C.white, border: L.rand, borderRadius: 8, marginBottom: 12 }}>
              {[
                ['ueberfaellig', 'Wartung überfällig', zaehler.ueberfaellig, C.red],
                ['bald', 'Wartung in 3 Monaten', zaehler.bald, C.orangeText],
                ['sicht', 'Sichtprüfungen fällig', zaehler.sicht, C.blue],
                ['ohne', 'Fällig ohne Vertrag', zaehler.ohneVertragFaellig, C.gray700],
              ].map(([schluessel, text, n, farbe], i) => (
                <button key={schluessel} type="button" onClick={() => kennzahlFilter(schluessel)}
                  style={{ flex: '1 1 180px', textAlign: 'left', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
                           borderLeft: i ? L.rand : 'none', fontFamily: 'inherit' }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: n ? farbe : C.gray300, lineHeight: 1.1 }}>{n}</div>
                  <div style={{ fontSize: 12, color: C.gray500 }}>{text}</div>
                </button>
              ))}
            </div>

            {/* Filterleiste */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', background: C.white, border: L.rand, borderRadius: 8, padding: 10, marginBottom: 12 }}>
              <div style={{ position: 'relative', flex: '1 1 240px' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: C.gray500, display: 'flex' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 18a7 7 0 1 1 0-14 7 7 0 0 1 0 14z M20 20l-4-4"/></svg>
                </span>
                <input value={suche} onChange={e => setSuche(e.target.value)} placeholder="Kunde, Kundennr., Station, Ort"
                  style={{ ...L.feld, width: '100%', paddingLeft: 32 }}/>
              </div>
              <select value={zeitraum} onChange={e => setZeitraum(e.target.value)} style={L.feld} aria-label="Zeitraum">
                {ZEITRAUM.map(z => <option key={z.key} value={z.key}>{z.text}</option>)}
              </select>
              {zeitraum === 'monat' && (
                <>
                  <select value={monat} onChange={e => setMonat(Number(e.target.value))} style={L.feld} aria-label="Monat">
                    <option value={0}>ganzes Jahr</option>
                    {MONATE_LANG.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                  <select value={jahr} onChange={e => setJahr(Number(e.target.value))} style={L.feld} aria-label="Jahr">
                    {jahre.map(j => <option key={j} value={j}>{j}</option>)}
                  </select>
                </>
              )}
              <select value={art} onChange={e => setArt(e.target.value)} style={L.feld} aria-label="Art">
                {ART.map(a => <option key={a.key} value={a.key}>{a.text}</option>)}
              </select>
              <select value={vertrag} onChange={e => setVertrag(e.target.value)} style={L.feld} aria-label="Vertrag">
                {VERTRAG.map(v => <option key={v.key} value={v.key}>{v.text}</option>)}
              </select>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.gray700, cursor: 'pointer', padding: '0 4px' }}>
                <input type="checkbox" checked={geplanteAusblenden} onChange={e => setGeplanteAusblenden(e.target.checked)} style={{ width: 15, height: 15, accentColor: C.navy }}/>
                geplante ausblenden
              </label>
            </div>

            <div style={{ fontSize: 12, color: C.gray500, margin: '0 2px 6px' }}>{liste.length} {liste.length === 1 ? 'Eintrag' : 'Einträge'}</div>

            <div style={{ background: C.white, border: L.rand, borderRadius: 8, overflow: 'hidden' }}>
              {liste.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: C.gray500, fontSize: 14 }}>Nichts gefunden.</div>}

              {liste.slice(0, 300).map((b, idx) => {
                const st = b.station;
                const g = GRUPPE[b.gruppe];
                const w = b.wartungsvertrag;
                const tel = st.objekt_telefon || st.kunden?.telefon || st.kunden?.mobil;
                const mail = st.objekt_email || st.kunden?.email;
                const zeiten = (w?.abschaltzeiten || []).map(k => ABSCHALTZEITEN.find(a => a.key === k)?.text).filter(Boolean);
                const monate = (w?.bevorzugte_monate || []).map(m => MONATE_KURZ[m - 1]).filter(Boolean);
                const imWunschmonat = b.faellig && (w?.bevorzugte_monate || []).includes(b.faellig.getMonth() + 1);
                const vertragsSchilder = [
                  b.vertraege.wartung?.vorhanden && 'Wartung',
                  b.vertraege.betriebsfuehrung?.vorhanden && 'Betriebsführung',
                  b.vertraege.bereitschaft?.vorhanden && 'Bereitschaft',
                ].filter(Boolean);
                // Ohne Migration 45: altes Haekchen an der Station anzeigen.
                if (vertragMigrationFehlt && st.wartungsvertrag) vertragsSchilder.push('Wartung');
                const diesenMonat = b.faellig && b.faellig.getFullYear() === new Date().getFullYear() && b.faellig.getMonth() === new Date().getMonth();
                const zusatz = [
                  (zeiten.length || monate.length || w?.abschalt_notiz)
                    && 'Abschaltung: ' + [zeiten.join(', '), monate.length ? 'bevorzugt ' + monate.join(', ') + (imWunschmonat ? ' ★' : '') : null, w?.abschalt_notiz].filter(Boolean).join(' · '),
                  w?.besondere_vereinbarungen && 'Vereinbarung: ' + w.besondere_vereinbarungen,
                  !w && st.wartungsvertrag && st.vertrag_notiz && 'Vertrag: ' + st.vertrag_notiz,
                  st.kunde_angesprochen_am && 'Angesprochen am ' + tagMonatJahr(st.kunde_angesprochen_am) + (st.kunde_angesprochen_notiz ? ' – ' + st.kunde_angesprochen_notiz : ''),
                ].filter(Boolean);
                return (
                  <div key={st.id + '-' + b.typ} style={{ display: 'flex', gap: 14, padding: '12px 16px', borderTop: idx ? L.rand : 'none', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <div style={{ width: 3, alignSelf: 'stretch', borderRadius: 2, background: g.farbe, flexShrink: 0 }}/>

                    {/* Wer / wo */}
                    <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>
                        {st.kunden?.name || '–'}
                        {st.kunden?.kunden_nr && <span style={{ marginLeft: 6, fontSize: 12, fontWeight: 400, color: C.gray500 }}>{st.kunden.kunden_nr}</span>}
                      </div>
                      <div style={{ fontSize: 13, color: C.gray700, marginTop: 1 }}>
                        {[st.bezeichnung, st.stations_nr && st.stations_nr !== st.bezeichnung ? st.stations_nr : null, fahrAnschrift(st)].filter(Boolean).join(' · ')}
                      </div>
                      {zusatz.map(z => <div key={z} style={{ fontSize: 12, color: C.gray500, marginTop: 3 }}>{z}</div>)}
                    </div>

                    {/* Wann */}
                    <div style={{ flex: '0 0 210px' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: b.typ === 'sicht' ? C.blue : C.gray500 }}>
                        {b.typ === 'sicht' ? 'Sichtprüfung' : 'Wartung'}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: g.farbe }}>
                        {monatName(b.faellig)}
                        {b.gruppe === 'ueberfaellig' && <span style={{ fontSize: 12, fontWeight: 600 }}> · überfällig</span>}
                        {diesenMonat && b.gruppe !== 'ueberfaellig' && <span style={{ fontSize: 12, fontWeight: 600 }}> · diesen Monat</span>}
                      </div>
                      <div style={{ fontSize: 12, color: C.gray500, marginTop: 1 }}>
                        {b.typ === 'wartung'
                          ? <>zuletzt {monatName(b.letzte)} · {b.zStation ? b.zStation + ' J.' : 'Zyklus –'}
                              {b.zEmpf && b.zEmpf !== b.zStation && <span style={{ color: b.zStation && b.zEmpf < b.zStation ? C.orangeText : C.gray500 }}> (empf. {b.zEmpf} J.)</span>}</>
                          : <>{w.sichtpruefung_jahre === 1 ? 'jährlich' : 'alle ' + w.sichtpruefung_jahre + ' J.'}{w.letzte_sichtpruefung ? ' · zuletzt ' + monatName(datumAus(w.letzte_sichtpruefung)) : ''}</>}
                      </div>
                      {b.geplant.length > 0 && (
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.green, marginTop: 2 }}>geplant {b.geplant.map(p => tagMonatJahr(p.wartungs_datum) || 'ohne Datum').join(', ')}</div>
                      )}
                    </div>

                    {/* Vertrag */}
                    <div style={{ flex: '0 0 120px', fontSize: 12, lineHeight: 1.5 }}>
                      {vertragsSchilder.length === 0
                        ? <span style={{ color: C.gray300 }}>kein Vertrag</span>
                        : vertragsSchilder.map(v => <div key={v} style={{ color: C.green }}>✓ {v}</div>)}
                    </div>

                    {/* Aktionen */}
                    <div style={{ flex: '0 0 270px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, marginLeft: 'auto' }}>
                      {onPlanen && (
                        <button type="button" style={L.knopf}
                          onClick={() => (b.typ === 'sicht'
                            ? onPlanen(st, { ...(planungAusVertrag(w) || {}), art: 'sichtpruefung', oelprobe: null, umz: false })
                            : onPlanen(st, planungAusVertrag(w)))}>
                          {b.typ === 'sicht' ? 'Sichtprüfung planen' : 'Wartung planen'}
                        </button>
                      )}
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {!vertragMigrationFehlt && <button type="button" onClick={() => setVertragOffen(st)} style={L.link}>Verträge</button>}
                        {b.typ === 'sicht' && (
                          <button type="button" onClick={() => sichtErledigt(b)} disabled={speichert === st.id} style={L.link} title="Sichtprüfung ohne Protokoll als erledigt eintragen">erledigt</button>
                        )}
                        {!b.hatVertrag && (
                          <button type="button" onClick={() => angesprochen(st)} disabled={speichert === st.id} style={L.link}>angesprochen</button>
                        )}
                        {tel && <a href={'tel:' + String(tel).replace(/[^\d+]/g, '')} style={L.link} title={tel}>Anrufen</a>}
                        {mail && <a href={'mailto:' + mail} style={L.link} title={mail}>E-Mail</a>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {liste.length > 300 && (
              <div style={{ padding: 12, textAlign: 'center', color: C.gray500, fontSize: 13 }}>
                Es werden die ersten 300 angezeigt – bitte über Suche oder Filter eingrenzen.
              </div>
            )}
          </>
        )}
      </div>

      {vertragOffen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.45)', zIndex: 200, overflowY: 'auto', padding: '24px 12px' }}>
          <div style={{ maxWidth: 960, margin: '0 auto', background: C.bg, borderRadius: 10, overflow: 'hidden', boxShadow: '0 12px 40px rgba(10,22,40,0.25)' }}>
            <div style={{ background: C.white, borderBottom: L.rand, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, zIndex: 1 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>Verträge</div>
                <div style={{ color: C.gray500, fontSize: 13 }}>{[vertragOffen.kunden?.name, vertragOffen.bezeichnung].filter(Boolean).join(' · ')}</div>
              </div>
              <button type="button" onClick={() => {
                if (ungespeichert.current && !window.confirm('Es gibt ungespeicherte Änderungen am Vertrag. Trotzdem schließen?')) return;
                ungespeichert.current = false;
                setVertragOffen(null); vertraegeLaden().catch(() => {});
              }} style={{ ...L.link, marginLeft: 'auto', fontSize: 14 }}>Schließen ✕</button>
            </div>
            <div style={{ padding: 16 }}>
              <VertraegeEditor stationId={vertragOffen.id} onAenderungen={(x) => { ungespeichert.current = x; }}/>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
